import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Container,
  Box,
  CircularProgress,
} from "@mui/material";
import { restaurantService } from "../../services/firebase/restaurantManagementService";
import { SaleItem } from "../../models/saleItem";
import { StringsService } from "../../services/strings/strings";
import AsyncString from "../../shared/asyncString";
import AsyncCardMedia from "../../shared/asyncCardMedia";
import { ConfirmActionDialog } from "../../shared/confirmActionDialog";
import { DateRangePill } from "../../shared/dateRangePill";
import { DiscountPill } from "../../shared/discountPill";
import { CategoryPill } from "../../shared/foodCategoryPill";
import { StatusPill } from "../../shared/statusPill";
import { FullProperty } from "../../models/fullProperty";
import { MasterDataService } from "../../services/firebase/masterdataService";
import { UpsertSaleItemDialog } from "./upsertSaleItemDialog";
import { updateTime } from "./upsertSaleItemHelpers";
import { useNavigate } from "react-router-dom";
import CountdownTimer from "../../listingDetailsPage/overviewCard/common/count_down_timer";

type MaintainSpecificRestaurantPageProps = {
  restaurant: FullProperty;
};

const MaintainSpecificRestaurantPage: React.FC<MaintainSpecificRestaurantPageProps> = ({ restaurant }) => {
  const [saleItems, setSaleItems] = useState<SaleItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [surplasBagDialogOpen, setSurplasBagDialogOpen] = useState<boolean>(false);
  const [fullMenuDialogOpen, setFullMenuDialogOpen] = useState<boolean>(false);
  const [confirmDeactivateDialogOpen, setConfirmDeactivateDialogOpen] = useState<boolean>(false);
  const [saleItemToBeDeactivated, setSaleItemToBeDeactivated] = useState<SaleItem | null>(null);
  const [editingItem, setEditingItem] = useState<boolean>(false);
  const [currentItem, setCurrentItem] = useState<SaleItem | null>(null);
  const [oldThumbnailImage, setOldThumbnailImage] = useState<string | null>(null);
  const [possibleDiscountReasons, setPossibleDiscountReasons] = useState<string[]>([]);
  const [possibleFoodCateory, setPossibleFoodCateory] = useState<string[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSaleItems();
  }, [restaurant.id]);


  const fetchSaleItems = async () => {
    try {
      setLoading(true);
      const data = await restaurantService.fetchSaleItems(restaurant.id!);
      setPossibleDiscountReasons(await MasterDataService.fetchSaleItemsDiscountReason());
      setPossibleFoodCateory(await MasterDataService.fetchFoodCategories());
      setSaleItems(data);
    } catch (err) {
      setError("Failed to fetch sale items.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSurplusBagDialog = async (item?: SaleItem) => {
    if (item) {
      setLoading(true);
      setEditingItem(true);
      const thumb = await StringsService.getStringById(`restaurant-${restaurant.id}-sale-item-${item.id}-thumbnail`)
      setOldThumbnailImage(thumb)

      const startOfCollectionTime = updateTime(
        item.startOfCollectionTime,
        item.startOfCollectionTimeDayDeltaFromNow
      );

      const endDate = updateTime(
        item.endDate,
        item.endDateDayDeltaFromNow
      );

      setCurrentItem({
        ...item,
        startOfCollectionTime: startOfCollectionTime.toISOString(),
        endDate: endDate.toISOString(),
      });
      setLoading(false);
    } else {
      setEditingItem(false);
      setCurrentItem({
        id: "",
        available: 0,
        isActive: true,
        updatedAt: new Date().toISOString(),
        createdOn: new Date().toISOString(),
        originalPrice: 0,
        discountPrice: 0,
        startOfCollectionTime: new Date().toISOString(),
        endDate: restaurant.closingTime,
        foodCategory: possibleFoodCateory[0],
        discountReason: possibleDiscountReasons[0],
        createdWithQuantity: 0,
      });
    }
    setSurplasBagDialogOpen(true);
  };

    const handleOpenFullMenuDialog = async (item?: SaleItem) => {
    if (item) {

    } else {
      setEditingItem(false);
      setCurrentItem({
        id: "",
        available: 0,
        isActive: true,
        updatedAt: new Date().toISOString(),
        createdOn: new Date().toISOString(),
        originalPrice: 0,
        discountPrice: 0,
        startOfCollectionTime: new Date().toISOString(),
        endDate: restaurant.closingTime,
        foodCategory: possibleFoodCateory[0],
        discountReason: possibleDiscountReasons[0],
        startOfCollectionTimeDayDeltaFromNow: 0,
        endDateDayDeltaFromNow: 0,
        createdWithQuantity: 0,
      });
    }
    setFullMenuDialogOpen(true);
  };

  const handleCloseDialog = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    setSurplasBagDialogOpen(false);
    setFullMenuDialogOpen(false);
    setCurrentItem(null);
    setOldThumbnailImage(null);
  };

  const handleRemoveItem = async (currentItem: SaleItem) => {
    try {
      if (restaurant.id) {
        currentItem.isActive = false;
        const newSaleItems = saleItems.map((item) => (item.id === currentItem.id ? currentItem : item));
        await restaurantService.editRestaurant(restaurant.id, { saleItems: newSaleItems });
        setSaleItems(newSaleItems);
      }
    } catch (error) {
      setError("Failed to remove sale item.");
    }
    handleCancelDeactivate()
  };

  const handleCancelDeactivate = () => {
    setSaleItemToBeDeactivated(null)
    setConfirmDeactivateDialogOpen(false)
  }

  return (
    <Box
      className="body"
      sx={{
        backgroundColor: "#F5F9E9",
        minHeight: "100vh",
        display: "flex",
        alignItems: "top",
        justifyContent: "center",
      }}
    >
      <Container>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Button
              variant="contained"
              sx={{ mt: 2, mb: 2, margin: "8px 8px", background: "#162C1F", border: "1px solid #162C1F", borderRadius: "24px" }}
              onClick={() => handleOpenSurplusBagDialog()}
            >
              Add Surprise Bage
            </Button>
            <Button
              variant="contained"
              sx={{ mt: 2, mb: 2, margin: "8px 8px", background: "#162C1F", border: "1px solid #162C1F", borderRadius: "24px" }}
              onClick={() => navigate(`/dashboard/${restaurant.id}/editMenu`)}
            >
              Add Menu
            </Button>
            {error && <Typography color="error">{error}</Typography>}
            <Grid container spacing={3} style={{ paddingTop: 16 }}>
              {saleItems.map((item) => {
                const startOfCollectionTimeUpdated = new Date(updateTime(item.startOfCollectionTime, item.startOfCollectionTimeDayDeltaFromNow));
                const endDateUpdated = new Date (updateTime(item.endDate, item.endDateDayDeltaFromNow));
                return <Grid item xs={12} sm={8} md={6} lg={4} xl={4} key={item.id}>
                  <Card
                    sx={{ maxWidth: 345, margin: "auto", cursor: "pointer" }}
                  >
                    <CardContent>
                      <Box sx={{ position: 'relative' }}>
                        <AsyncCardMedia
                          component="img"
                          sx={{ height: 200 }}
                          imageId={"restaurant-" + restaurant.id + "-sale-item-" + item.id + "-thumbnail"}
                          alt="Sale item thumbnail"
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 8,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            paddingX: 2,
                            paddingY: 0.5,
                            borderRadius: '16px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            backdropFilter: 'blur(4px)',
                          }}
                        >
                          Available: {item.available}
                        </Box>
                      </Box>

                      <Typography variant="h6" gutterBottom>
                        <AsyncString stringId={"restaurant-" + restaurant.id + "-saleItem-" + item.id + "-sale-item-name"} />
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
                        <AsyncString stringId={`restaurant-${restaurant.id}-saleItem-${item.id}-sale-item-description`} />
                      </Typography>
                      <DateRangePill startDate={startOfCollectionTimeUpdated} endDate={endDateUpdated} />
                      <CountdownTimer endDate={endDateUpdated} isClosed={false} />
                      <DiscountPill originalPrice={item.originalPrice} discountPrice={item.discountPrice} />
                      <CategoryPill foodCategory={item.foodCategory} />
                      <StatusPill isActive={item.isActive} />
                      <Typography>
                        Price:&nbsp;
                        <span style={{ textDecoration: 'line-through', marginRight: 8 }}>
                          {new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(item.originalPrice / 100)}
                        </span>
                        <span style={{ color: 'red', fontWeight: 'bold' }}>
                          {new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(item.discountPrice / 100)}
                        </span>
                      </Typography>
                      <Button
                        variant="contained"
                        sx={{ mt: 1, background: "#162C1F", border: "1px solid #162C1F", borderRadius: "24px" }}
                        fullWidth
                        onClick={() => handleOpenSurplusBagDialog(item)}
                      >
                        Edit
                      </Button>
                      {item.isActive &&
                        <Button
                          variant="outlined"
                          sx={{ mt: 1, border: "1px solid #162C1F", borderRadius: "24px" }}
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation();
                            setSaleItemToBeDeactivated(item)
                            setConfirmDeactivateDialogOpen(true);
                          }}

                        >
                          <Typography variant="button" component="h1" sx={{ color: "#162C1F" }}>
                            DEACTIVATE
                          </Typography>
                        </Button>
                      }
                    </CardContent>
                  </Card>
                </Grid>
              })}
            </Grid>
          </>
        )}
        {currentItem && (
          <UpsertSaleItemDialog
            open={surplasBagDialogOpen}
            onClose={handleCloseDialog}
            onSuccess={fetchSaleItems}
            restaurant={restaurant}
            editingItem={editingItem}
            currentItem={currentItem}
            saleItems={saleItems}
            setSaleItems={setSaleItems}
            possibleDiscountReasons={possibleDiscountReasons}
            possibleFoodCateory={possibleFoodCateory}
            initialThumbnail={oldThumbnailImage}
          />
        )}
        {saleItemToBeDeactivated &&
          <ConfirmActionDialog
            isOpen={confirmDeactivateDialogOpen}
            onClose={() => handleCancelDeactivate()}
            onConfirm={() => handleRemoveItem(saleItemToBeDeactivated)}
            title="Confirm Removal"
          >
            <Box>
              <div>Are you sure you want to remove the sale item:</div>
              <div style={{ fontWeight: 'bold' }}>
                <AsyncString stringId={`restaurant-${restaurant.id}-saleItem-${saleItemToBeDeactivated.id}-sale-item-name`} />
              </div>
            </Box>
          </ConfirmActionDialog>
        }
      </Container>
    </Box>
  );
};

export default MaintainSpecificRestaurantPage;